import { IndexedMap } from '@basehub/utils/indexed-map/index'
import { AgentBlock, Block, RootBlock, getInstanceChildId } from '.'
import {
  BlockRelationship,
  CommonBRsCompoundIndexes,
  getBRId,
} from '../block-relationship'
import { DEFAULT_START_MODEL } from '../repository'

export const SYSTEM_AGENTS: [AgentBlock, ...AgentBlock[]] = [
  {
    id: 'START',
    type: 'agent',
    title: 'START',
    sourceBlockId: 'START',
    description: 'The built-in agent made by BaseHub.',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    isCommitted: false,
    hidden: false,
    typeOptions: {
      avatar: null,
      model: DEFAULT_START_MODEL,
    },
    value: {
      // should be kept in sync with the default prompt in apps/start/src/chat/chat-loop.ts
      state: JSON.stringify({
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: 'The assistant is START, created by BaseHub.',
              },
            ],
          },
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: "START is an expert copywriter, content strategist, and technical writer. START enjoys creating websites using modern tech stacks, and integrating them with the BaseHub CMS. Why? Because BaseHub makes it easy to iterate on the website's content and again, START loves writing content for the web. START has unlimited resources and can keep going until the user's query is completely resolved.",
              },
            ],
          },
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: '<about_basehub>',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: "BaseHub is a Headless CMS. With building blocks, users can create flexible structures that map to a website's tree-like structure. Editors interact with content via the dashboard, while developers can use the GraphQL API to query and/or mutate content. START interacts via API.",
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '</about_basehub>',
              },
            ],
          },
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: '<response_guidelines>',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '1. Respond with clean, properly formatted content that can be directly displayed in the chat interface. You can use markdown formatting in your responses.',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '2. You can tag specific users by their ID, like "@<user-id>[username]" (the username is optional).',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '3. You can tag/link to specific blocks in the repository by their ID, like #<block-id>[block-title] (the title is optional). When talking about a block, prefer tagging it instead of just mentioning it by name, as it helps users navigate the repository.',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '</response_guidelines>',
              },
            ],
          },
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text: '<interacting_with_users>',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '1. In every thread, you\'ll be interacting with one or more users. Because the thread is multi-user, you don\'t need to answer every single message. Maybe, the message is not directed at you, and therefore you should not respond (you can use the "staySilent" tool for this).',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '2. User info can be queried using the "getUserInfo" tool. User ID and username are not the same as, say, an "Author" created in the CMS; but sometimes, it can be useful to grab the name of the BaseHub user so then you can use its Author object for the content you write.',
              },
              {
                type: 'hardBreak',
              },
              {
                type: 'text',
                text: '</interacting_with_users>',
              },
            ],
          },
        ],
      }),
    },
    constraints: {
      edit: 'enabled',
      searchTheWeb: 'enabled',
      getUserInfo: 'enabled',
      commit: 'disabled',
      manageBranches: 'disabled',
      customTools: {},
      access: {},
    },
  },
]

export function injectBuiltInAgents(params: {
  rootBlock: RootBlock
  blockRelationshipsMap: IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  blocksMap: IndexedMap<Block>
}): Set<string>

export function injectBuiltInAgents({
  blockRelationshipsMap,
  blocksMap,
  rootBlock,
}: {
  rootBlock: RootBlock
  blockRelationshipsMap: IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  blocksMap: IndexedMap<Block>
}) {
  const injected = new Set<string>()
  for (const agent of SYSTEM_AGENTS) {
    // Check if agent already exists
    const existingBr = blockRelationshipsMap.get(agent.id)
    const existingBlock = existingBr ? blocksMap.get(existingBr.blockId) : null

    if (!existingBlock) {
      // Create virtual block
      const virtualBlock: AgentBlock & { __virtual?: boolean } = {
        ...agent,
        __virtual: true,
      }

      // Create virtual block relationship
      const newBr: BlockRelationship & { __virtual?: boolean } = {
        validFrom: new Date().toISOString(),
        validTo: undefined,
        branchId: 'main',
        blockId: virtualBlock.id,
        id: getBRId(virtualBlock.id, rootBlock.id),
        isCommitted: false,
        order: 'Zz', // agents are always at the end
        parentId:
          ('sourceBlockId' in rootBlock
            ? rootBlock.sourceBlockId
            : rootBlock.id) || rootBlock.id,
        sourceBlockId: virtualBlock.id,
        deletedAt: null,
        structural: false,
        __virtual: true,
      }
      // Add to maps
      blocksMap.set(virtualBlock.id, virtualBlock)
      blockRelationshipsMap.set(newBr.id, newBr)
      injected.add(newBr.id)
    }
    if (existingBr) {
      injected.add(existingBr.id)
    }
  }
  return injected
}

export const getAgentBlockCoWId = ({
  targetSourceBlockId,
  rootBlock,
}: {
  targetSourceBlockId: string
  rootBlock: RootBlock
}) => {
  return getInstanceChildId({
    parentSourceBlockId: rootBlock.sourceBlockId || rootBlock.id,
    targetSourceBlockId,
  })
}

export const makeAgentBlockCopy = ({
  agentId,
  rootBlock,
  isoNow,
}: {
  agentId: string
  rootBlock: RootBlock
  isoNow: string
}) => {
  const _builtInAgent = SYSTEM_AGENTS.find((a) => a.id === agentId)
  if (_builtInAgent) {
    const builtInAgent = structuredClone(_builtInAgent)
    const block: AgentBlock = {
      ...builtInAgent,
      sourceBlockId: builtInAgent.sourceBlockId || builtInAgent.id,
      createdAt: isoNow,
      updatedAt: isoNow,
      hidden: false,
      isCommitted: false,
      id: getAgentBlockCoWId({
        targetSourceBlockId: builtInAgent.sourceBlockId || builtInAgent.id,
        rootBlock,
      }),
    }
    const br: BlockRelationship = {
      branchId: 'main',
      validFrom: isoNow,
      validTo: undefined,
      blockId: block.id,
      id: getBRId(block.id, rootBlock.sourceBlockId || rootBlock.id),
      isCommitted: false,
      parentId: rootBlock.sourceBlockId || rootBlock.id,
      sourceBlockId: agentId,
      order: 'Zz', // agents are always at the end
      structural: false, // cause virtual
    }

    return { br, block }
  }

  return {}
}
